import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import type * as Y from 'yjs';
import {
  isAudioPath,
  isImagePath,
  isMarkdownPath,
  isVideoPath,
  parseFrontmatter,
  titleFromPath,
} from '@rbv/shared';
import { vault } from '../sync/vault.js';
import { vaultFileUrl } from '../navigation.js';
import { CanvasMarkdown } from './markdown.jsx';
import { makeTapDetector, startDrag } from './drag.js';
import { containsRect, resolveColor, snap, type CanvasNode, type Pt } from './model.js';
import type { CanvasStore } from './store.js';

export interface NodeViewProps {
  node: CanvasNode;
  store: CanvasStore;
  selected: boolean;
  editing: boolean;
  /** Current viewport scale (read at gesture start). */
  getScale: () => number;
  onSelect: (id: string) => void;
  onEditNode: (id: string | null) => void;
  onOpenNote: (id: string) => void;
}

function useYText(yt: Y.Text | null): string {
  return useSyncExternalStore(
    (cb) => {
      if (!yt) return () => {};
      const h = () => cb();
      yt.observe(h);
      return () => yt.unobserve(h);
    },
    () => (yt ? yt.toString() : ''),
  );
}

function hostOf(url: string | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

function FileNodeContent({
  node,
  onSelect,
}: {
  node: CanvasNode;
  onSelect: () => void;
}) {
  const path = node.file ?? '';
  const fileId = path ? vault.findByPath(path) : null;
  const isMd = isMarkdownPath(path);
  const yt = isMd && fileId ? vault.ytext(fileId) : null;
  const content = useYText(yt);

  if (!path) return <div className="cv-missing muted">No file</div>;
  if (isImagePath(path)) {
    return <img className="cv-img" src={vaultFileUrl(path)} alt={titleFromPath(path)} draggable={false} />;
  }
  if (isVideoPath(path)) {
    return (
      <video
        className="cv-media"
        src={vaultFileUrl(path)}
        controls
        playsInline
        preload="metadata"
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
  if (isAudioPath(path)) {
    return (
      <audio
        className="cv-media cv-audio"
        src={vaultFileUrl(path)}
        controls
        preload="metadata"
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
  if (isMd) {
    if (!fileId) {
      return (
        <div className="cv-missing muted">
          {path}
          <br />
          (missing)
        </div>
      );
    }
    const { bodyStart } = parseFrontmatter(content);
    return (
      <>
        <div className="cv-file-header">{titleFromPath(path)}</div>
        <div
          className="cv-file-body cv-scroll"
          onPointerDown={(e) => e.stopPropagation()}
          onPointerUp={onSelect}
        >
          <CanvasMarkdown md={content.slice(bodyStart)} />
        </div>
      </>
    );
  }
  return (
    <div className="cv-link-body">
      <div className="cv-link-title">{titleFromPath(path)}</div>
      <div className="cv-link-url muted">{path}</div>
    </div>
  );
}

export function NodeView(props: NodeViewProps) {
  const { node, store, selected, editing, getScale, onSelect, onEditNode, onOpenNote } = props;
  const tapRef = useRef(makeTapDetector());
  const isGroup = node.type === 'group';
  const color = resolveColor(node.color);

  // Inline editing counts as an interaction: defer remote reloads.
  useEffect(() => {
    if (!editing) return;
    store.beginInteraction();
    return () => store.endInteraction();
  }, [editing, store]);

  const handleTap = (ev: PointerEvent) => {
    if (!tapRef.current(ev.clientX, ev.clientY)) return;
    // Double-tap / double-click actions per node type.
    if (node.type === 'text') {
      onEditNode(node.id);
    } else if (node.type === 'link' && node.url) {
      window.open(node.url, '_blank', 'noopener');
    } else if (node.type === 'file' && node.file && isMarkdownPath(node.file)) {
      const fileId = vault.findByPath(node.file);
      if (fileId) onOpenNote(fileId);
    } else if (node.type === 'group') {
      const label = window.prompt('Group label', node.label ?? '');
      if (label !== null) {
        store.apply((m) => {
          const n = m.nodes.find((x) => x.id === node.id);
          if (n) n.label = label.trim() ? label : undefined;
        });
      }
    }
  };

  const onPointerDown = (e: ReactPointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    if (editing) return;
    onSelect(node.id);
    const scale = getScale();
    // Drag set: the node itself, plus contained members for groups.
    const startPositions = new Map<string, Pt>();
    startPositions.set(node.id, { x: node.x, y: node.y });
    if (isGroup) {
      for (const other of store.model.nodes) {
        if (other.id !== node.id && containsRect(node, other)) {
          startPositions.set(other.id, { x: other.x, y: other.y });
        }
      }
    }
    const sx = e.clientX;
    const sy = e.clientY;
    let began = false;
    startDrag(e, {
      move: (ev) => {
        if (!began) {
          began = true;
          store.beginInteraction();
        }
        const dx = (ev.clientX - sx) / scale;
        const dy = (ev.clientY - sy) / scale;
        store.apply((m) => {
          for (const n of m.nodes) {
            const p = startPositions.get(n.id);
            if (p) {
              n.x = snap(p.x + dx);
              n.y = snap(p.y + dy);
            }
          }
        });
      },
      up: (ev, moved) => {
        if (began) store.endInteraction();
        if (!moved && ev.type !== 'pointercancel') handleTap(ev);
      },
    });
  };

  const style: CSSProperties = {
    left: node.x,
    top: node.y,
    width: node.width,
    height: node.height,
  };
  if (color) {
    style.borderColor = color;
    style.background = isGroup
      ? `color-mix(in srgb, ${color} 7%, transparent)`
      : `color-mix(in srgb, ${color} 12%, var(--bg-surface))`;
  }

  let content: ReactNode;
  switch (node.type) {
    case 'text':
      content = (
        <div className="cv-text-body">
          <CanvasMarkdown md={node.text ?? ''} />
        </div>
      );
      break;
    case 'file':
      content = <FileNodeContent node={node} onSelect={() => onSelect(node.id)} />;
      break;
    case 'link':
      content = (
        <div className="cv-link-body">
          <div className="cv-link-title">{node.label ?? hostOf(node.url)}</div>
          <a
            className="cv-link-url"
            href={node.url}
            target="_blank"
            rel="noopener noreferrer"
            onPointerDown={(e) => e.stopPropagation()}
          >
            {node.url}
          </a>
        </div>
      );
      break;
    case 'group':
      content = (
        <div
          className="cv-group-label"
          style={color ? { borderColor: color } : undefined}
          onPointerDown={onPointerDown}
        >
          {node.label ?? 'Group'}
        </div>
      );
      break;
    default:
      content = <div className="cv-missing muted">Unsupported node type: {node.type}</div>;
  }

  return (
    <div
      className={`cv-node cv-node-${node.type}${selected ? ' cv-selected' : ''}`}
      style={style}
      onPointerDown={isGroup ? undefined : onPointerDown}
    >
      {content}
      {editing && node.type === 'text' && (
        <textarea
          className="cv-edit"
          defaultValue={node.text ?? ''}
          autoFocus
          onFocus={(e) => {
            const v = e.currentTarget.value;
            e.currentTarget.setSelectionRange(v.length, v.length);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (e.key === 'Escape') e.currentTarget.blur();
          }}
          onBlur={(e) => {
            const v = e.currentTarget.value;
            store.apply((m) => {
              const n = m.nodes.find((x) => x.id === node.id);
              if (n) n.text = v;
            });
            onEditNode(null);
          }}
        />
      )}
    </div>
  );
}
